//#region Imports

import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { InputComponent } from '../../../../../shared/components/input/input.component';
import { ClassForm } from '../../../../../shared/models/interfaces/forms/course-form.interface';
import { fetchOEmbedPreview, getChannelInitials, VideoEmbedPreview } from '../../../../../shared/helpers/video-embed.helper';
import {
  VIDEO_EMBED_PROVIDER_CONFIG,
  VideoEmbedProvider,
  VideoEmbedProviderConfig,
} from './course-content-type-video-embed.constants';

//#endregion

@Component({
  selector: 'l-course-content-type-video-embed',
  standalone: true,
  templateUrl: './course-content-type-video-embed.component.html',
  imports: [ReactiveFormsModule, InputComponent],
})
export class CourseContentTypeVideoEmbedComponent implements OnDestroy {

  //#region Inputs

  @Input({ required: true })
  public form!: FormGroup<ClassForm>;

  @Input({ required: true })
  public provider!: VideoEmbedProvider;

  @Input()
  public preview: VideoEmbedPreview | null = null;

  //#endregion

  //#region Outputs

  @Output()
  public readonly previewChange = new EventEmitter<VideoEmbedPreview | null>();

  //#endregion

  //#region Public Properties

  public isLoading: boolean = false;

  //#endregion

  //#region Private Properties

  private activeRequestToken: string | null = null;

  //#endregion

  //#region Getters

  public get config(): VideoEmbedProviderConfig {
    return VIDEO_EMBED_PROVIDER_CONFIG[this.provider];
  }

  public get urlControl(): FormControl<string> {
    return this.form.controls[this.config.urlControlName];
  }

  public get isLoaded(): boolean {
    return this.preview !== null;
  }

  //#endregion

  //#region Lifecycle Hooks

  public ngOnDestroy(): void {
    this.activeRequestToken = null;
  }

  //#endregion

  //#region Public Methods

  public onVideoUrlChange(value: string): void {
    const normalizedValue = value.trim();
    this.urlControl.setValue(value);
    this.activeRequestToken = null;

    if (!normalizedValue || !this.config.isUrlValid(normalizedValue)) {
      this.isLoading = false;
      this.previewChange.emit(null);
      return;
    }

    this.isLoading = true;
    this.previewChange.emit(null);
    void this.resolvePreview(normalizedValue);
  }

  public getChannelInitials(channelName: string): string {
    return getChannelInitials(channelName, this.config.channelInitialsFallback);
  }

  //#endregion

  //#region Private Methods

  private async resolvePreview(url: string): Promise<void> {
    const request = `${this.config.requestTokenPrefix}:${crypto.randomUUID()}`;
    this.activeRequestToken = request;

    try {
      const resolvedPreview = await fetchOEmbedPreview(this.config.buildOEmbedEndpoint(url));

      if (this.activeRequestToken !== request) {
        return;
      }

      this.isLoading = false;
      this.previewChange.emit(resolvedPreview);
    } catch {
      if (this.activeRequestToken !== request) {
        return;
      }

      this.isLoading = false;
      this.previewChange.emit(null);
    }
  }

  //#endregion

}
